import {sound as Sound} from '@pixi/sound';
import {IBrowser, IService, DeviceType, DeviceQuality, SystemComponentType} from '@gongxigames/framework/core/types';
import {errorReporter} from '@gongxigames/framework/core/utils';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {BufferResourceWebP} from '@gongxigames/framework/core/fragments/WebP/ResourceWebP';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';

declare type DeviceCallback = () => void;

export const DEVICE_QUALITY = {
    [DeviceQuality.SUPER_LOW]: 'SUPER_LOW',
    [DeviceQuality.LOW]: 'LOW',
    [DeviceQuality.HIGH]: 'HIGH',
    [DeviceQuality.MEDIUM]: 'MEDIUM',

    getDefinition(quality: DeviceQuality): string {
        return this[quality];
    }
};

const SUPPORTED_AUDIO_FORMATS = ['ogg', 'm4a'];
const AUDIO_FORMATS = ['mp3', 'm4a', 'opus', 'wav'];
const testImageSourcesWebP = [
    'data:image/webp;base64,UklGRjIAAABXRUJQVlA4ICYAAACyAgCdASoCAAEALmk0mk0iIiIiIgBoSygABc6zbAAA/v56QAAAAA==',
    'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAQAAAAfQ//73v/+BiOh/AAA='
];

const testImageSourcesAvif = [
    // eslint-disable-next-line max-len
    'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABgAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACBtZGF0EgAKCBgABggQEDQgMgof8D///8QAAK9w'
];

@injectable()
export class Device implements IService {
    public static NAME: symbol = CoreDI.Device;
    public type: SystemComponentType = 'service';
    /**
     * Initialization has completed
     */
    private _initialized = false;

    private _desktop = false;
    private _iOS = false;
    private _iosChrome = false;

    /**
     * If running in iOS this will contain the major version number
     */
    private _iOSVersion?: string;

    private _android = false;
    private _chromeOS = false;
    private _linux = false;
    private _macOS = false;
    private _windows = false;
    private _windowsPhone = false;
    private _canvas = false;
    private _chrome = false;

    private _isWebGLHardwareAccelerated = false;
    private _webGL = false;

    /**
     * If running in Chrome this will contain the major version number
     */
    private _chromeVersion?: number;

    private _firefox = false;

    /**
     * If running in Firefox this will contain the major version number
     */
    private _firefoxVersion?: number;

    /**
     *  Set to true if running in Internet Explorer
     */
    private _ie = false;

    /**
     * If running in Internet Explorer this will contain the major version number.
     * Beyond IE10 you should use this.trident and this.tridentVersion
     */
    private _ieVersion?: number;

    /**
     * Set to true if running a Trident version of Internet Explorer (IE11+)
     */
    private _trident = false;

    /**
     * If running in Internet Explorer 11 this will contain the major version number.
     * See {@link http://msdn.microsoft.com/en-us/library/ie/ms537503(v=vs.85).aspx}
     */
    private _tridentVersion?: number;

    /**
     * Set to true if running in Microsoft Edge browser
     */
    private _edge = false;

    /**
     * Set to true if running in Mobile Safari
     */
    private _mobileSafari = false;
    private _opera = false;
    private _safari = false;

    /**
     * If running in Safari this will contain the major version number
     */
    private _safariVersion?: number;

    /**
     * Set to true if running as a WebApp, i.e. within a WebView
     */
    private _webApp = false;

    /**
     * Are Audio tags available?
     */
    private _audioData = false;

    /**
     * If supports WebAudio API this will contain AudioContext constructor?
     */
    private _webAudioContextConstructor!: typeof AudioContext;

    /**
     * Can this device play ogg files?
     */
    private _ogg = false;

    /**
     * opus - Can this device play opus files?
     */
    private _opus = false;

    /**
     * Can this device play mp3 files?
     */
    private _mp3 = false;

    /**
     * Can this device play wav files?
     */
    private _wav = false;

    /**
     * Can this device play m4a files?
     */
    private _m4a = false;
    private _iPhone = false;
    private _iPhone4 = false;
    private _iPad = false;
    private _samsungS8 = false;
    private _pixelRatio = window.devicePixelRatio ?? 1;
    private _webP = false;
    private _avif = false;

    /**
     * Does the browser support the Full Screen API?
     */
    private _fullscreen = false;

    /**
     * If the browser supports the Full Screen API this holds the call you need to use to activate it
     */
    private _requestFullscreen!: string;

    /**
     * If the browser supports the Full Screen API this holds the call you need to use to cancel it
     */
    private _cancelFullscreen!: string;

    /**
     * The Element that is currently being presented in full-screen mode in this document
     */
    private _fullscreenElement!: string;

    /**
     * Does the browser support access to the Keyboard during Full Screen mode?
     */
    private _fullscreenKeyboard = false;
    private _onInitialized!: DeviceCallback;
    private _deviceType!: DeviceType;
    private _quality!: DeviceQuality;

    @inject(CoreDI.Browser)
    private _browser!: IBrowser;
    private _readyCheckListener: () => void = this._readyCheck.bind(this);

    public configure(): Promise<void> {
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            // why is there an additional timeout here?
            this._browser.addTimeout(this._readyCheckListener);
        } else {
            this._browser.addEventListener('DOMContentLoaded', this._readyCheckListener);
            this._browser.addEventListener('load', this._readyCheckListener);
        }

        return new Promise(resolve => {
            this._onInitialized = resolve;
        });
    }

    /**
     * check whether the host environment can play audio
     * @param type One of 'mp3, 'ogg', 'wav', 'opus'
     * @returns {boolean}
     */
    public canPlayAudio(type: string): boolean {
        if (type === 'mp3' && this._mp3) {
            return true;
        } else if (type === 'm4a' && this._m4a) {
            return true;
        } else if (type === 'ogg' && (this._ogg || this._opus)) {
            return true;
        } else if (type === 'opus' && this._opus) {
            return true;
        } else if (type === 'wav' && this._wav) {
            return true;
        }

        return false;
    }

    public getSoundFormat(): string {
        if (this.canPlayAudio('ogg')) {
            return 'ogg';
        }

        if (this.canPlayAudio('m4a')) {
            return 'm4a';
        }

        return '';
    }

    public get isSoundSupported(): boolean {
        return SUPPORTED_AUDIO_FORMATS.some(this.canPlayAudio.bind(this));
    }

    public isSupported(): boolean {
        return this.webGL && !this.ie;
    }

    protected getSupportedAudioFormat(): string {
        let format = '';

        for (const audioFormat of AUDIO_FORMATS) {
            if (this.canPlayAudio(audioFormat)) {
                format = audioFormat;
                break;
            }
        }

        return format;
    }

    public getSoundQuality(): string {
        if (Sound.useLegacy) {
            return 'low';
        } else if (this.desktop) {
            return 'hi';
        } else {
            return 'mid';
        }
    }

    public getReportParams(): Record<string, string|boolean> {
        const pixelRatio = this.getPixelRatio();

        return {
            'fw:deviceType': this._deviceType,
            'fw:quality': DEVICE_QUALITY.getDefinition(this._quality),
            'fw:soundFormat': this.getSoundFormat(),
            'fw:soundQuality': this.getSoundQuality(),
            'fw:screenRaw': `${screen.width}x${screen.height}`,
            'fw:screen': `${screen.width * pixelRatio}x${screen.height * pixelRatio}`,
            'fw:pixelRatio': `${pixelRatio}`
        };
    }

    public getPixelRatio(): number {
        return this._pixelRatio;
    }

    public get deviceType(): DeviceType {
        return this._deviceType;
    }

    public get canvas(): boolean {
        return this._canvas;
    }

    public get webGL(): boolean {
        return this._webGL;
    }

    public get isWebGLHardwareAccelerated(): boolean {
        return this._isWebGLHardwareAccelerated;
    }

    public get webp(): boolean {
        return this._webP;
    }

    public get avif(): boolean {
        return this._avif;
    }

    public set webGL(value: boolean) {
        this._webGL = value;
    }

    public get chrome(): boolean {
        return this._chrome;
    }

    public get firefox(): boolean {
        return this._firefox;
    }

    public get audioData(): boolean {
        return this._audioData;
    }

    public get webAudioContextConstructor(): typeof AudioContext {
        return this._webAudioContextConstructor;
    }

    public get cancelFullscreen(): string {
        return this._cancelFullscreen;
    }

    public get fullscreenElement(): string {
        return this._fullscreenElement;
    }

    public get mobileSafari(): boolean {
        return this._mobileSafari;
    }

    public get webApp(): boolean {
        return this._webApp;
    }

    public get android(): boolean {
        return this._android;
    }

    public get iPhone4(): boolean {
        return this._iPhone4;
    }

    public get iPhone(): boolean {
        return this._iPhone;
    }

    public get iOS(): boolean {
        return this._iOS;
    }

    public get fullscreen(): boolean {
        return this._fullscreen;
    }

    public get requestFullscreen(): string {
        return this._requestFullscreen;
    }

    public get desktop(): boolean {
        return this._desktop;
    }

    public get iPad(): boolean {
        return this._iPad;
    }

    public get isMobile(): boolean {
        return !this._desktop;
    }

    public get isIOSChrome(): boolean {
        return this._iosChrome;
    }

    public get isMacOs(): boolean {
        return this._macOS;
    }

    public get isAppleDevice(): boolean {
        return this.isMacOs || this.iOS;
    }

    public get isSuperLowQuality(): boolean {
        return this.quality === DeviceQuality.SUPER_LOW;
    }

    public get quality(): DeviceQuality {
        return this._quality;
    }

    public get ie(): boolean {
        return this._ie;
    }

    public get samsungS8(): boolean {
        return this._samsungS8;
    }

    public get screenViewportWidth(): number {
        return this.iPhone && this.isIOSChrome ? document.documentElement.clientWidth : window.innerWidth;
    }

    public get screenViewportHeight(): number {
        return this.iPhone && this.isIOSChrome ? document.documentElement.clientHeight : window.innerHeight;
    }

    public get info(): Record<string, string> {
        return {
            resolution: `${this.screenViewportWidth} / ${this.screenViewportWidth}`
        };
    }

    public get iOSVersion(): string {
        return this._iOSVersion || '';
    }

    public get isInIframe(): boolean {
        return window.self !== window.top;
    }

    private _readyCheck(): void {
        if (!document.body) {
            this._browser.addTimeout(this._readyCheckListener, 20);
        } else if (!this._initialized) {
            this._browser.removeEventListener('DOMContentLoaded', this._readyCheckListener);
            this._browser.removeEventListener('load', this._readyCheckListener);

            this._initialize();
        }
    }

    private async _initialize(): Promise<void> {
        this._initialized = true;

        // Run the checks
        this._checkOS();
        this._checkBrowser();
        this._checkAudio();
        this._checkDevice();
        this._checkFeatures();
        this._checkFullScreenSupport();
        this._checkType();
        this._checkDeviceQuality();
        await this._checkWebP();
        await this._checkAvif();

        errorReporter.setTags(this.getReportParams());

        if (!this._webP) {
            await BufferResourceWebP.init();
        }

        this._onInitialized();
    }

    private _checkOS(): void {
        const ua = navigator.userAgent;

        if (/Playstation Vita/.test(ua)) {
            // this.vita = true;
        } else if (/Kindle/.test(ua) || /\bKF[A-Z][A-Z]+/.test(ua) || /Silk.*Mobile Safari/.test(ua)) {
            // this.kindle = true;
            // This will NOT detect early generations of Kindle Fire, I think there is no reliable way...
            // E.g. "Mozilla/5.0 (Macintosh; U; Intel Mac OS X 10_6_3; en-us; Silk/1.1.0-80)
            // AppleWebKit/533.16 (KHTML, like Gecko) Version/5.0 Safari/533.16 Silk-Accelerated=true"
        } else if (/Android/.test(ua)) {
            this._android = true;
        } else if (/CrOS/.test(ua)) {
            this._chromeOS = true;
        } else if (/iP[ao]d|iPhone/i.test(ua) || (ua.toLowerCase().indexOf('macintosh') > -1 && 'ontouchend' in document)) {
            this._iOS = true;
            (navigator.appVersion).match(/OS ([\d|_]+) /);
            this._iOSVersion = RegExp.$1;
        } else if (/Linux/.test(ua)) {
            this._linux = true;
        } else if (/Mac OS/.test(ua)) {
            this._macOS = true;
        } else if (/Windows/.test(ua)) {
            this._windows = true;
        }

        if (/Windows Phone/i.test(ua) || /IEMobile/i.test(ua)) {
            this._android = false;
            this._iOS = false;
            this._macOS = false;
            this._windows = true;
            this._windowsPhone = true;
        }

        const silk = /Silk/.test(ua); // detected in browsers

        if (this._windows || this._macOS || (this._linux && !silk) || this._chromeOS) {
            this._desktop = true;
        }

        // Windows Phone / Table reset
        if (this._windowsPhone || ((/Windows NT/i.test(ua)) && (/Touch/i.test(ua)))) {
            this._desktop = false;
        }

        this._iosChrome = ua.indexOf('CriOS') !== -1;
    }

    private _checkFullScreenSupport(): void {
        const checkAPI = (parameters: string[], element: any = document): any => {
            for (const value of parameters) {
                if (element[value]) {
                    return element[value];
                }
            }

            return false;
        };
        const API = {
            enabled: [
                'fullscreenEnabled',
                'webkitFullscreenEnabled',
                'mozFullScreenEnabled',
                'msFullscreenEnabled'
            ]
        };

        if (this._macOS || !checkAPI(API.enabled)) {
            return;
        }

        const fs = [
            'requestFullscreen',
            'requestFullScreen',
            'webkitRequestFullscreen',
            'webkitRequestFullScreen',
            'msRequestFullscreen',
            'msRequestFullScreen',
            'mozRequestFullScreen',
            'mozRequestFullscreen'
        ];

        const element = document.createElement('div');

        for (const fsValue of fs) {
            if ((element as any)[fsValue]) {
                this._fullscreen = true;
                this._requestFullscreen = fsValue;
                break;
            }
        }

        const cfs = [
            'cancelFullScreen',
            'exitFullscreen',
            'webkitCancelFullScreen',
            'webkitExitFullscreen',
            'msCancelFullScreen',
            'msExitFullscreen',
            'mozCancelFullScreen',
            'mozExitFullscreen'
        ];

        if (this._fullscreen) {
            for (const cfsValue of cfs) {
                if (cfsValue in document) {
                    this._cancelFullscreen = cfsValue;
                    break;
                }
            }
        }

        const fse = [
            'fullscreenElement',
            'fullScreenElement',
            'webkitFullscreenElement',
            'webkitCurrentFullScreenElement',
            'mozFullScreenElement',
            'msFullscreenElement'
        ];

        if (this._fullscreen) {
            for (const fseValue of fse) {
                if (fseValue in document) {
                    this._fullscreenElement = fseValue;
                    break;
                }
            }
        }

        // Keyboard Input?
        if ((window as any).Element && (window as any).Element.ALLOW_KEYBOARD_INPUT) {
            this._fullscreenKeyboard = true;
        }
    }

    private _checkBrowser(): void {
        const ua = navigator.userAgent;

        if (/Edge\/\d+/.test(ua)) {
            this._edge = true;
        } else if ((/Chrome\/(\d+)/.test(ua) || ua.match('CriOS')) && !this._windowsPhone) {
            this._chrome = true;
            this._chromeVersion = parseInt(RegExp.$1, 10);
        } else if (/Firefox\D+(\d+)/.test(ua) || /FxiOS\D+(\d+)/.test(ua)) {
            this._firefox = true;
            this._firefoxVersion = parseInt(RegExp.$1, 10);
        } else if (/AppleWebKit/.test(ua) && this._iOS) {
            this._mobileSafari = true;
        } else if (/MSIE (\d+\.\d+);/.test(ua)) {
            this._ie = true;
            this._ieVersion = parseInt(RegExp.$1, 10);
        } else if (/Opera/.test(ua)) {
            this._opera = true;
        } else if (/Safari\/(\d+)/.test(ua) && !this._windowsPhone) {
            this._safari = true;

            if (/Version\/(\d+)\./.test(ua)) {
                this._safariVersion = parseInt(RegExp.$1, 10);
            }
        } else if (/Trident\/(\d+\.\d+)(.*)rv:(\d+\.\d+)/.test(ua)) {
            this._ie = true;
            this._trident = true;
            this._tridentVersion = parseInt(RegExp.$1, 10);
            this._ieVersion = parseInt(RegExp.$3, 10);
        }

        // WebApp mode in iOS
        if ((navigator as any).standalone) {
            this._webApp = true;
        }
    }

    private _checkAudio(): void {
        this._audioData = !!((window as any).Audio);
        this._webAudioContextConstructor = ((window as any).AudioContext || (window as any).webkitAudioContext);

        const audioElement = document.createElement('audio');

        try {
            if (audioElement.canPlayType) {
                if (audioElement.canPlayType('audio/ogg; codecs="vorbis"').replace(/^no$/, '')) {
                    this._ogg = true;
                }

                if (audioElement.canPlayType('audio/ogg; codecs="opus"')
                    .replace(/^no$/, '') || audioElement.canPlayType('audio/opus;').replace(/^no$/, '')) {
                    this._opus = true;
                }

                if (audioElement.canPlayType('audio/mpeg;').replace(/^no$/, '')) {
                    this._mp3 = true;
                }

                if (audioElement.canPlayType('audio/wav; codecs="1"').replace(/^no$/, '')) {
                    this._wav = true;
                }

                if (audioElement.canPlayType('audio/x-m4a;') || audioElement.canPlayType('audio/aac;').replace(/^no$/, '')) {
                    this._m4a = true;
                }
            }
        } catch (e) {
            consoleLogger.warn('Device check audio exception');
        }
    }

    private _checkDevice(): void {
        const ua = navigator.userAgent.toLowerCase();

        this._iPhone = ua.includes('iphone');
        this._iPhone4 = (this._pixelRatio === 2 && this._iPhone);
        this._iPad = ua.includes('ipad') || (ua.includes('macintosh') && 'ontouchend' in document);
        this._samsungS8 = ua.indexOf('SM-G955F') > -1 || ua.indexOf('SM-G950F') > -1;
    }

    private _checkFeatures(): void {
        this._canvas = !!(window as any).CanvasRenderingContext2D;

        try {
            const canvas: HTMLCanvasElement = document.createElement('canvas');
            const options = {stencil: true};
            const gl: RenderingContext | null = canvas.getContext('webgl2', options) ||
                canvas.getContext('webgl', options) ||
                canvas.getContext('experimental-webgl', options);

            if (gl) {
                if (this._desktop) {
                    this._isWebGLHardwareAccelerated = this.isHardwareAccelerationOnDesktopDevices(gl);
                }
            }

            this._webGL = !!gl;
        } catch (e) {}
    }

    private isHardwareAccelerationOnDesktopDevices(gl: any): boolean {
        const debugInfo = (gl as any).getExtension('WEBGL_debug_renderer_info');
        const renderer = (gl as any).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
        // list of main renderers
        const hardwareAccelerationDevices = ['intel', 'amd', 'apple', 'nvidia'];

        return hardwareAccelerationDevices.some(device => renderer.includes(device));
    }

    private async _checkImageFormat(imgSources: string[]): Promise<boolean> {
        let isFormat = false;

        await Promise
            .all(imgSources.map(dataUrl => {

                return new Promise(resolve => {
                    const img = document.createElement('img');

                    img.onerror = () => resolve(false);
                    img.onload = () => resolve(true);
                    img.src = dataUrl;
                });
            }))
            .then(results => isFormat = results.every(res => res))
            .catch(() => isFormat = false);

        return isFormat;
    };

    private async _checkWebP(): Promise<void> {
        if (this._iOS || this._macOS) {
            return;
        }

        this._webP = await this._checkImageFormat(testImageSourcesWebP);
    }

    private async _checkAvif(): Promise<void> {
        this._avif = await this._checkImageFormat(testImageSourcesAvif);
    }

    private _checkType(): void {
        if (this._desktop) {
            this._deviceType = DeviceType.DESKTOP;
        } else {
            this._deviceType = DeviceType.MOBILE;
        }
    }

    private _checkDeviceQuality(): void {
        const ratio = window.devicePixelRatio || 1;
        const screenWidth = Math.max(screen.width, screen.height) * ratio;

        if (this._desktop) {
            if (this._ie || screenWidth < 1300) {
                this._quality = DeviceQuality.LOW;
            } else {
                this._quality = DeviceQuality.HIGH;
            }
        } else {
            if (screenWidth < 1000) {
                this._quality = DeviceQuality.SUPER_LOW;
            } else if (screenWidth < 1300) {
                this._quality = DeviceQuality.LOW;
            } else {
                this._quality = DeviceQuality.HIGH;
            }
        }
    }
}
